import React, { useRef, useState } from 'react';
import { Upload, Loader2, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { api, assetUrl } from '../lib/api';
import { toast } from 'sonner';

/* ---------- helpers ---------- */
const IMAGE_KEYS = ['image', 'logo', 'icon_image', 'hero_image', 'intro_image', 'milestones_image', 'badge'];

function isImagePath(key, value) {
  const k = key.toLowerCase();
  if (IMAGE_KEYS.some((n) => k === n || k.endsWith(n))) return true;
  if (typeof value === 'string' && value.indexOf('/uploads/') !== -1) return true;
  return false;
}

function isVideoField(key) {
  return key.toLowerCase().indexOf('video') !== -1;
}

const LONG_KEYS = ['paragraph', 'text', 'bio', 'subtitle', 'description', 'excerpt', 'body', 'message', 'caption'];

function isLongText(key, value) {
  if (typeof value !== 'string') return false;
  if (value.length > 90) return true;
  const k = key.toLowerCase();
  return LONG_KEYS.some((n) => k.indexOf(n) !== -1);
}

function labelize(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function blankFrom(template) {
  if (typeof template === 'string') return '';
  if (typeof template === 'number') return 0;
  if (typeof template === 'boolean') return false;
  if (Array.isArray(template)) return [];
  if (template && typeof template === 'object') {
    const out = {};
    Object.keys(template).forEach((k) => {
      out[k] = blankFrom(template[k]);
    });
    return out;
  }
  return '';
}

const INPUT_CLS = 'w-full h-10 rounded-lg bg-white border border-[#E6E8EE] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B]';
const LABEL_CLS = 'text-xs font-bold text-black/70 uppercase tracking-wide';

/* ---------- Image field with upload ---------- */
export function ImageField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url);
      toast.success('File uploaded');
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.detail) || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const preview = value ? (
    <img src={assetUrl(value)} alt="" className="h-14 w-20 object-cover rounded-lg border border-[#E6E8EE] bg-white shrink-0" />
  ) : (
    <div className="h-14 w-20 rounded-lg border border-dashed border-[#c9cdd6] bg-[#F6F7F9] shrink-0" />
  );

  return (
    <div className="flex items-center gap-3">
      {preview}
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/api/uploads/..."
        className="flex-1 h-10 rounded-lg bg-white border border-[#E6E8EE] px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#26247B]"
      />
      <button
        type="button"
        data-testid="admin-media-upload-button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-[#26247B] text-white text-xs font-bold transition-colors duration-200 hover:bg-[#1f1d66] disabled:opacity-60 shrink-0"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        <span>Upload</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*,video/mp4,video/webm,.pdf" className="hidden" onChange={handleFile} />
    </div>
  );
}

/* ---------- String field ---------- */
function StringField({ fieldKey, value, onChange }) {
  const label = <label className={LABEL_CLS}>{labelize(fieldKey)}</label>;

  if (isImagePath(fieldKey, value)) {
    return (
      <div className="space-y-1.5">
        {label}
        <ImageField value={value} onChange={onChange} />
      </div>
    );
  }

  if (isVideoField(fieldKey)) {
    return (
      <div className="space-y-1.5">
        <label className={LABEL_CLS}>{labelize(fieldKey)} (YouTube / Vimeo / MP4 URL)</label>
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className={INPUT_CLS} />
      </div>
    );
  }

  if (isLongText(fieldKey, value)) {
    const rows = Math.min(8, Math.max(2, Math.ceil(value.length / 90)));
    return (
      <div className="space-y-1.5">
        {label}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full rounded-lg bg-white border border-[#E6E8EE] px-3 py-2.5 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#26247B]"
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label}
      <input value={value} onChange={(e) => onChange(e.target.value)} className={INPUT_CLS} />
    </div>
  );
}

/* ---------- Array field ---------- */
function ArrayField({ fieldKey, value, onChange, depth }) {
  const [collapsed, setCollapsed] = useState(depth > 1);

  const addItem = () => {
    const template = value.length > 0 ? value[value.length - 1] : '';
    onChange(value.concat([blankFrom(template)]));
  };

  const removeItem = (index) => {
    onChange(value.filter((_, j) => j !== index));
  };

  const updateItem = (index, newItem) => {
    onChange(value.map((v, j) => (j === index ? newItem : v)));
  };

  return (
    <div className="border border-[#E6E8EE] rounded-xl bg-[#FAFBFC]">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-[#26247B]"
      >
        <span>{labelize(fieldKey)} ({value.length} items)</span>
        {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-4 space-y-4">
          {value.map((item, i) => (
            <div key={i} className="relative border border-[#E6E8EE] rounded-lg bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-black/50">Item {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Remove</span>
                </button>
              </div>
              {React.createElement(FieldEditor, {
                fieldKey: fieldKey.replace(/s$/, ''),
                value: item,
                depth: depth + 1,
                onChange: (newItem) => updateItem(i, newItem),
              })}
            </div>
          ))}
          <button type="button" onClick={addItem} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2E8B57] hover:underline">
            <Plus className="h-4 w-4" />
            <span>Add item</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Object field ---------- */
function ObjectField({ fieldKey, value, onChange, depth }) {
  return (
    <div className={depth === 0 ? 'space-y-5' : 'space-y-4'}>
      {depth > 0 && <p className="text-xs font-bold text-black/50 uppercase tracking-wide">{labelize(fieldKey)}</p>}
      {Object.keys(value).map((k) =>
        React.createElement(FieldEditor, {
          key: k,
          fieldKey: k,
          value: value[k],
          depth: depth + 1,
          onChange: (newVal) => onChange({ ...value, [k]: newVal }),
        })
      )}
    </div>
  );
}

/* ---------- Recursive dispatcher ---------- */
export function FieldEditor({ fieldKey, value, onChange, depth = 0 }) {
  if (typeof value === 'string') {
    return <StringField fieldKey={fieldKey} value={value} onChange={onChange} />;
  }
  if (typeof value === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
        <span>{labelize(fieldKey)}</span>
      </label>
    );
  }
  if (typeof value === 'number') {
    return (
      <div className="space-y-1.5">
        <label className={LABEL_CLS}>{labelize(fieldKey)}</label>
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className={INPUT_CLS} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return <ArrayField fieldKey={fieldKey} value={value} onChange={onChange} depth={depth} />;
  }
  if (value && typeof value === 'object') {
    return <ObjectField fieldKey={fieldKey} value={value} onChange={onChange} depth={depth} />;
  }
  return null;
}
